import { For, Show, createMemo, createSignal, type Component } from 'solid-js';

import { freeCells, usedCells } from '../game/container';
import { equipItem, RARITY_ORDER, type InventoryItem, type ItemRarity } from '../net/protocol';
import type { GameStore } from '../state/store';
import { meetsRequirements, preferredSlot, slotLabel, unmetRequirements } from './character';
import { isUpgrade } from './compare';
import { ItemGrid } from './ItemGrid';
import { Purse } from './Purse';
import { ItemTooltipBody } from './ItemTooltip';
import { PaperDoll } from './PaperDoll';
import { EMPTY_FILTER, matchesFilter } from './stash';

/** Rarity choices for the "at least this good" filter. */
const RARITY_LABELS: Record<ItemRarity, string> = {
  NORMAL: 'Normal+',
  MAGIC: 'Magic+',
  RARE: 'Rare+',
  UNIQUE: 'Unique',
};

/**
 * Gear and bag: the paper doll, and the grid of what the player is carrying, on `I`.
 *
 * **This replaced the "Account Vault" that used to open on this key**, and the rename is
 * the feature rather than a cosmetic. There was one flat hundred-slot list that was both
 * the thing you carried into a dungeon and the thing you kept between runs, so nothing
 * was ever a decision: every drop fitted, nothing had to be left behind, and there was
 * no reason to go home except a full belt. The bag is now 10 × 4 with footprints, and the
 * vault is a separate place in town — see `VaultSection` and
 * `doc/grind/architecture/CONTAINERS-GRID-INVENTORY.md`.
 *
 * The doll is here rather than on the character sheet because equipping is a drag from
 * the grid onto a socket, and `menu` deliberately holds one panel at a time. See
 * `PaperDoll.tsx`.
 *
 * Filtering **dims** rather than removes, which is the rule from
 * `design/HUD-UX.md` and is now structural as well as preferred: an item has a
 * cell, so hiding one would leave a hole that looks free and is not.
 */
export const BagPanel: Component<{ store: GameStore }> = (props) => {
  const [search, setSearch] = createSignal('');
  const [minRarity, setMinRarity] = createSignal<ItemRarity | null>(null);

  const filter = createMemo(() => ({
    ...EMPTY_FILTER,
    search: search(),
    minRarity: minRarity(),
  }));

  const bag = () => props.store.bag();
  const matches = createMemo(() => bag().items.filter((it) => matchesFilter(it, filter())).length);
  const attributes = () => props.store.character()?.attributes.total ?? null;

  /** Which socket an item would go to, or null when nothing it fits is free. */
  const slotFor = (item: InventoryItem) =>
    preferredSlot(item, (s) => props.store.equipped(s) != null);

  /**
   * Whether this character can actually wear it — **the slot _and_ the gate**.
   *
   * Reported: *it is always saying that we can equip an item even though we don't have
   * requirements.* `slotFor` answers a different question from the one the bag was asking with
   * it. It says whether a socket of the right *kind* exists, which is true for every chest piece
   * ever generated, so the "unwearable" marker only ever fired for an item that fits nowhere at
   * all — and an attribute gate never made anything unwearable in the bag.
   *
   * The server was right the whole time: `World.equip` re-checks every gate and refuses, so this
   * was never an exploit. It was worse than an exploit in one way — the client invited the click,
   * the server declined it, and nothing on screen explained the disagreement.
   *
   * `attributes()` is null before the first CHARACTER frame. Unknown attributes must **not**
   * read as "fails the gate", or every item in the bag would be struck through for the first
   * frames after entering a zone; an item with no gate at all is wearable either way.
   */
  const wearable = (item: InventoryItem) => {
    if (slotFor(item) === null) return false;
    const total = attributes();
    if (!total) return true;
    return meetsRequirements(total, item.requires);
  };

  /** What already occupies that socket, for the comparison. */
  const worn = (item: InventoryItem) => {
    const target = slotFor(item);
    return target ? (props.store.equipped(target)?.item ?? undefined) : undefined;
  };

  return (
    <Show when={props.store.menu() === 'stash'}>
      <div class="stash" data-testid="bag-panel">
        <div class="plate plate--riveted stash__panel hud-interactive">
          <div class="stash__head">
            <h2 class="stash__title">Gear &amp; Bag</h2>
            <span class="stash__count" data-testid="bag-count">
              {matches()} / {bag().items.length} shown · {usedCells(bag())} of{' '}
              {bag().cols * bag().rows} cells
            </span>
            <button
              type="button"
              class="sheet__close"
              data-testid="bag-close-x"
              onClick={() => props.store.closeMenu()}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/*
            The doll and the grid, **side by side**.

            Side by side because equipping is a drag between them, and because stacked they
            measured 800px in a real browser against an 813px viewport — so the free-space
            line and the footer were scrolled off the bottom of a panel that looked
            complete. jsdom computes no layout, so nothing in the vitest suite could have
            seen that; it was found by measuring, which is the rule in `doc/grind/architecture/TESTING-STRATEGY.md`.
          */}
          <div class="stash__body">
            <PaperDoll store={props.store} />

            <div class="stash__carried">
              <div class="stash__controls">
                <input
                  class="stash__search"
                  type="search"
                  placeholder="Search name, base or affix…"
                  aria-label="Search your bag"
                  data-testid="bag-search"
                  value={search()}
                  onInput={(e) => setSearch(e.currentTarget.value)}
                />
                <select
                  class="stash__rarity"
                  aria-label="Minimum rarity"
                  data-testid="bag-rarity"
                  value={minRarity() ?? ''}
                  onChange={(e) =>
                    setMinRarity((e.currentTarget.value || null) as ItemRarity | null)
                  }
                >
                  <option value="">Any rarity</option>
                  <For each={RARITY_ORDER}>
                    {(r) => <option value={r}>{RARITY_LABELS[r]}</option>}
                  </For>
                </select>
              </div>

              <ItemGrid
                store={props.store}
                container={bag()}
                testId="bag"
                dimmed={(it) => !matchesFilter(it, filter())}
                // Marked, never disabled. An item you cannot wear is exactly the one whose
                // stats you want to read, so it keeps its hover, its tooltip and its cells.
                unwearable={(it) => !wearable(it)}
                // A higher item level than what is worn. Item level rather than a stat
                // total: the simulation does not read most affixes yet, so a "better"
                // computed from them would be a confident lie.
                //
                // **Gated by `wearable`, not by `slotFor`**: an item you cannot lift is not an
                // upgrade, and an arrow over one is the same lie the marker was telling.
                badge={(it) => wearable(it) && isUpgrade(it, worn(it))}
                // Click still equips, and it has to: a drag needs a mouse, and every cell
                // is a focusable button, so this is the keyboard path. Nothing happens for
                // an item with nowhere to go, or one whose gate this character misses — the
                // tooltip already says which.
                onActivate={(it) => {
                  const target = slotFor(it);
                  if (target && wearable(it)) props.store.send(equipItem(it.uuid, target));
                }}
                tooltip={(it) => (
                  <>
                    <ItemTooltipBody
                      item={it}
                      attributes={attributes()}
                      worn={worn(it)}
                      toughness={props.store.character()?.derived.toughness}
                    />
                    <p class="doll__tip-note">
                      {/* Three states, and the gate one is new. It names what is missing rather
                          than saying "you cannot": the player's next question is always how
                          much more, and `unmetRequirements` already answers it. */}
                      {slotFor(it) === null
                        ? 'Nothing you have can wear this.'
                        : wearable(it)
                          ? `Click or drag to ${slotLabel(slotFor(it)!)}.`
                          : `Requires ${unmetRequirements(attributes()!, it.requires).join(' · ')}.`}
                    </p>
                  </>
                )}
              />

              {/* How full it is, in the unit that decides whether the next drop fits. A
                  count would not: eleven rings and one greatsword both read as "12 items"
                  and only one of them leaves room for another. */}
              <p class="stash__space" data-testid="bag-space">
                <Show when={freeCells(bag()) > 0} fallback={<strong>No room left.</strong>}>
                  <strong>{freeCells(bag())}</strong> free cell{freeCells(bag()) === 1 ? '' : 's'}
                </Show>{' '}
              </p>
            </div>
          </div>

          <hr class="rule" />

          {/*
            **The purse on its own line, in evidence.**

            It was a small figure appended to "96 free cells", which is two numbers on one
            line with nothing to tell them apart at a glance. Gold is what a full bag
            converts into — the decision the panel exists to support — so it gets a line and
            a size of its own rather than a suffix.
          */}
          <div class="stash__purse">
            <Purse gold={props.store.gold()} label="gold" testId="bag-gold" />
          </div>

          <div class="stash__foot">
            {/* The only place either modifier is discoverable, so both are named. */}
            <span class="stash__hint" data-testid="bag-hint">
              Drag to move · drop on a socket to wear · alt-click to lock · ctrl-click to drop ·
              Esc or I to close
            </span>
            <button
              type="button"
              class="sigil-button"
              data-testid="bag-close"
              onClick={() => props.store.closeMenu()}
            >
              <span>Close</span>
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
};
